"use client";

import * as React from "react";

import { Box } from "@salestrak/ui";

export type MapLayerPreviewVariant =
  | "terrain"
  | "satellite"
  | "roads"
  | "places"
  | "transit"
  | "traffic"
  | "property";

/* eslint-disable no-restricted-syntax -- Art DEPICTING a map, hand-drawn:
   nothing here encodes data, so the chart kit has no part to play. The colours
   are the map's own — parchment ground, white roads, transit blue — and must
   not follow the current theme: a Terrain tile has to look like terrain while
   the app is dark, which is the entire point of the preview. */
const GROUND = "#eeeae0";
const ROAD = "#ffffff";
const ROAD_EDGE = "#d9d4c6";
const LABEL = "#9c968a";
const TERRAIN = {
  ground: "#e9e5d8", low: "#cbd8b8", high: "#dce4cb",
  contour: "#a9ba96", water: "#a9cdd5", shore: "#d6e5df",
};
const SATELLITE = {
  ground: "#33402a",
  field: "#3f4d31",
  wood: "#2a3522",
  earth: "#4a4132",
  crop: "#37452c",
  roof: "#5d5343",
  road: "#b9b3a2",
  water: "#344e50",
  shore: "#657966",
  shadow: "#263226",
  roofLight: "#aaa18c",
};
const PLACES = { food: "#e9772b", shop: "#3b82f6", park: "#2f9e5b" };
const TRANSIT = { line: "#2b66d8", branch: "#8b3fd1" };
const TRAFFIC = { free: "#2f9e5b", slow: "#e8a621", jam: "#d9362b" };
const PROPERTY = { card: "#ffffff", edge: "#d9d4c6", bar: "#9c968a", pin: "#d9362b" };

const WIDTH = 140;
const HEIGHT = 72;

/** A ground with one crossing of roads — the stage the detail layers share. */
function Streets(): React.ReactElement {
  return (
    <g>
      <rect fill={GROUND} height={HEIGHT} width={WIDTH} />
      <path d="M0 40 H140 M56 0 V72" stroke={ROAD} strokeWidth={8} />
      <path d="M0 40 H140 M56 0 V72" stroke={ROAD_EDGE} strokeWidth={1} />
    </g>
  );
}

const ART: Record<MapLayerPreviewVariant, React.ReactElement> = {
  terrain: (
    <g>
      <rect fill={TERRAIN.ground} height={HEIGHT} width={WIDTH} />
      <path d="M0 0H93C83 10 88 19 68 25S45 23 34 39 16 56 0 52Z" fill={TERRAIN.low} />
      <path d="M0 0H69C59 9 68 16 47 20S28 22 19 34 6 39 0 39Z" fill={TERRAIN.high} />
      <path d="M92 72C91 56 102 48 119 49S137 39 140 36V72Z" fill={TERRAIN.high} />
      <g fill="none" stroke={TERRAIN.contour} strokeWidth={0.65} opacity={0.65}>
        <path d="M-5 47C20 54 25 25 44 28S78 25 80 14 92 1 97-4" />
        <path d="M-5 35C13 42 21 16 39 18S65 16 65 8 76 0 77-4" />
        <path d="M-5 24C9 29 14 9 31 10S52 9 52-4" />
        <path d="M-5 12C8 16 9 1 24 2S37 0 38-4" />
        <path d="M99 77C95 60 108 53 120 55S138 46 145 43" />
        <path d="M108 77C104 65 114 61 125 62S139 54 146 52" />
      </g>
      <path d="M100-5C90 12 113 19 99 33S75 52 83 77" fill="none" stroke={TERRAIN.shore} strokeWidth={9} />
      <path d="M100-5C90 12 113 19 99 33S75 52 83 77" fill="none" stroke={TERRAIN.water} strokeWidth={5} />
      <path d="M-5 61C21 60 29 42 50 43S91 54 110 36 129 26 145 28M45 43L38 72M113 34L127 4" fill="none" stroke={ROAD_EDGE} strokeWidth={4.5} />
      <path d="M-5 61C21 60 29 42 50 43S91 54 110 36 129 26 145 28M45 43L38 72M113 34L127 4" fill="none" stroke={ROAD} strokeWidth={2.5} />
    </g>
  ),
  satellite: (
    <g>
      <rect fill={SATELLITE.ground} height={HEIGHT} width={WIDTH} />
      <path d="M0 0H46L38 28 0 36Z" fill={SATELLITE.field} />
      <path d="M49 0H91L80 29 42 27Z" fill={SATELLITE.crop} />
      <path d="M0 40L35 32 48 72H0Z" fill={SATELLITE.earth} />
      <path d="M112 0H140V37L123 45 110 25Z" fill={SATELLITE.field} />
      <path d="M94 50L118 44 140 55V72H89Z" fill={SATELLITE.wood} />
      <g stroke={SATELLITE.field} strokeWidth={0.7} opacity={0.7}>
        <path d="M4 45L12 72M10 43L19 72M16 41L26 72M22 40L33 72M28 38L40 72" />
        <path d="M51 5L84 9M49 10L82 14M47 15L80 19M45 20L78 24" />
      </g>
      <path d="M100-5C90 12 113 19 99 33S75 52 83 77" fill="none" stroke={SATELLITE.shore} strokeWidth={9} />
      <path d="M100-5C90 12 113 19 99 33S75 52 83 77" fill="none" stroke={SATELLITE.water} strokeWidth={6} />
      <path d="M-5 61C21 60 29 42 50 43S91 54 110 36 129 26 145 28M45 43L38 72M113 34L127 4" fill="none" stroke={SATELLITE.shadow} strokeWidth={5} />
      <path d="M-5 61C21 60 29 42 50 43S91 54 110 36 129 26 145 28M45 43L38 72M113 34L127 4" fill="none" stroke={SATELLITE.road} strokeWidth={2.5} />
      <g fill={SATELLITE.shadow} transform="translate(1 1.5)">
        <path d="M43 32H51V38H43ZM55 34H63V40H55ZM67 35H74V41H67ZM49 51H57V57H49ZM61 53H69V59H61Z" />
        <path d="M127 17L130 10 136 13 133 20ZM123 27L126 20 132 23 129 30ZM128 36H137V43H128Z" />
      </g>
      <g fill={SATELLITE.roofLight}>
        <path d="M43 32H51V38H43ZM67 35H74V41H67ZM49 51H57V57H49Z" />
        <path d="M127 17L130 10 136 13 133 20ZM128 36H137V43H128Z" />
      </g>
      <g fill={SATELLITE.roof}>
        <path d="M55 34H63V40H55ZM61 53H69V59H61ZM123 27L126 20 132 23 129 30Z" />
      </g>
      <g fill={SATELLITE.wood}>
        <circle cx={10} cy={12} r={5} /><circle cx={17} cy={8} r={6} />
        <circle cx={24} cy={12} r={4} /><circle cx={8} cy={21} r={6} />
        <circle cx={17} cy={23} r={4} /><circle cx={132} cy={57} r={6} />
        <circle cx={123} cy={60} r={5} /><circle cx={138} cy={65} r={7} />
      </g>
    </g>
  ),
  roads: (
    <g>
      <Streets />
      <rect fill={LABEL} height={4} rx={1} width={36} x={84} y={38} />
      <rect fill={LABEL} height={4} rx={1} width={28} x={12} y={38} />
      <rect fill={LABEL} height={16} rx={1} width={4} x={54} y={50} />
    </g>
  ),
  places: (
    <g>
      <Streets />
      <circle cx={24} cy={18} fill={PLACES.food} r={7} />
      <circle cx={24} cy={18} fill={ROAD} r={2.5} />
      <circle cx={108} cy={16} fill={PLACES.shop} r={7} />
      <circle cx={108} cy={16} fill={ROAD} r={2.5} />
      <circle cx={30} cy={58} fill={PLACES.park} r={7} />
      <circle cx={30} cy={58} fill={ROAD} r={2.5} />
    </g>
  ),
  transit: (
    <g>
      <Streets />
      <path
        d="M0 56 C 40 56, 60 30, 140 32"
        fill="none"
        stroke={TRANSIT.line}
        strokeLinecap="round"
        strokeWidth={4}
      />
      <path
        d="M0 64 C 40 64, 60 40, 140 42"
        fill="none"
        stroke={TRANSIT.branch}
        strokeLinecap="round"
        strokeWidth={4}
      />
      <rect fill={TRANSIT.line} height={18} rx={4} width={18} x={100} y={6} />
      <path
        d="M104.5 20 V10 L109 15 L113.5 10 V20"
        fill="none"
        stroke={ROAD}
        strokeLinejoin="round"
        strokeWidth={2.2}
      />
    </g>
  ),
  traffic: (
    <g>
      <Streets />
      <path d="M0 40 H56" stroke={TRAFFIC.free} strokeWidth={4} />
      <path d="M56 40 H140" stroke={TRAFFIC.slow} strokeWidth={4} />
      <path d="M56 0 V40" stroke={TRAFFIC.jam} strokeWidth={4} />
      <path d="M56 40 V72" stroke={TRAFFIC.free} strokeWidth={4} />
    </g>
  ),
  property: (
    <g>
      <Streets />
      {/* A parcel's fact card floating over its lot, tail to the pin dot. */}
      <circle cx={96} cy={54} fill={PROPERTY.pin} r={4} />
      <rect
        fill={PROPERTY.card}
        height={34}
        rx={4}
        stroke={PROPERTY.edge}
        width={58}
        x={70}
        y={10}
      />
      <rect fill={PROPERTY.bar} height={4} rx={1} width={30} x={77} y={17} />
      <rect fill={PROPERTY.edge} height={4} rx={1} width={44} x={77} y={26} />
      <rect fill={PROPERTY.edge} height={4} rx={1} width={36} x={77} y={35} />
    </g>
  ),
};

/**
 * A miniature of one map layer, drawn for {@link PreviewRadioGroup} cards and
 * {@link PreviewArtThumbnail} rows. Wide (140×72) so a card shows the whole
 * scene; a squarer frame crops to the centre (`slice`), where every variant
 * keeps its telling detail. Decorative — the host names the layer.
 */
export function MapLayerPreviewArt({
  variant,
}: {
  variant: MapLayerPreviewVariant;
}): React.ReactElement {
  return (
    <svg
      aria-hidden="true"
      className="block"
      height="100%"
      preserveAspectRatio="xMidYMid slice"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      width="100%"
    >
      {ART[variant]}
    </svg>
  );
}

/**
 * The card-sized frame for {@link MapLayerPreviewArt}: the art's own aspect,
 * so a `PreviewRadioGroup` card is as tall as the picture and no taller.
 */
export function MapLayerPreviewFrame({
  variant,
}: {
  variant: MapLayerPreviewVariant;
}): React.ReactElement {
  return (
    <Box className="aspect-[140/72]" width="full">
      <MapLayerPreviewArt variant={variant} />
    </Box>
  );
}
